import React from "react";
import { PenLine } from "lucide-react";
import { fitBeam } from "@/lib/beams";

// Hand-built, labelled notation diagrams for the beginner note-value and time-signature pages.
const LINE0 = 70, SP = 20, BOT = LINE0 + 4 * SP;
const BW = 46;
const ACC = "hsl(var(--primary))";
const STEPS = { E4: 0, F4: 1, G4: 2, A4: 3, B4: 4, C5: 5, D5: 6, E5: 7, F5: 8 };
const yOf = (p) => BOT - STEPS[p] * (SP / 2);
const TREBLE = ["M70,100 C70,78 88,70 93,90", "M93,90 C96,112 80,122 68,120", "M68,120 C50,118 44,98 56,84", "M56,84 C68,70 86,56 84,30 C82,14 70,12 66,26", "M66,26 C64,40 74,110 78,140 C80,152 66,158 60,150"];

const Clef = () => (
  <g transform={`translate(-30 ${LINE0 - 40})`} fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round">
    {TREBLE.map((d) => <path key={d} d={d} />)}<circle cx="62" cy="147" r="6" fill="currentColor" stroke="none" />
  </g>
);
const Lines = ({ x1, x2 }) => [0, 1, 2, 3, 4].map((k) => <line key={k} x1={x1} x2={x2} y1={LINE0 + k * SP} y2={LINE0 + k * SP} stroke="currentColor" strokeWidth="1.6" />);
const TimeSig = ({ x, top, bottom }) => (
  <g fontFamily="Georgia, serif" fontWeight="900" fontSize="44" textAnchor="middle" fill="currentColor">
    <text x={x} y={LINE0 + 2 * SP - 4}>{top}</text><text x={x} y={BOT - 4}>{bottom}</text>
  </g>
);
// kind: c crotchet, m minim, s semibreve, q quaver
const Note = ({ x, p = "B4", kind, beam, dotted }) => {
  const y = yOf(p), up = beam != null || STEPS[p] < 4, filled = kind === "c" || kind === "q";
  const sx = up ? x + 10.5 : x - 10.5, ey = beam ?? (up ? y - 68 : y + 68);
  return (
    <g>
      <ellipse cx={x} cy={y} rx={kind === "s" ? 14 : 12} ry="9" transform={`rotate(-20 ${x} ${y})`} fill={filled ? "currentColor" : "none"} stroke="currentColor" strokeWidth={kind === "s" ? 4.5 : 3.5} />
      {kind !== "s" && <line x1={sx} x2={sx} y1={y} y2={ey} stroke="currentColor" strokeWidth="2.6" />}
      {dotted && <circle cx={x + 22} cy={STEPS[p] % 2 === 0 ? y - 5 : y} r="3.6" fill="currentColor" />}
      {kind === "q" && beam == null && (up
        ? <path d={`M${sx},${ey} c2,14 20,20 14,40`} fill="none" stroke="currentColor" strokeWidth="3.2" />
        : <path d={`M${sx},${ey} c2,-14 20,-20 14,-40`} fill="none" stroke="currentColor" strokeWidth="3.2" />)}
    </g>
  );
};
const Barline = ({ x, double }) => double
  ? <g><line x1={x - 9} x2={x - 9} y1={LINE0} y2={BOT} stroke="currentColor" strokeWidth="2" /><rect x={x - 5} y={LINE0} width="6" height={BOT - LINE0} fill="currentColor" /></g>
  : <line x1={x} x2={x} y1={LINE0} y2={BOT} stroke="currentColor" strokeWidth="2" />;
const Arrow = ({ x1, y1, x2, y2 }) => {
  const a = Math.atan2(y2 - y1, x2 - x1), h = 9;
  return (
    <g stroke={ACC} strokeWidth="2.2" fill={ACC}>
      <line x1={x1} y1={y1} x2={x2} y2={y2} />
      <polygon stroke="none" points={`${x2},${y2} ${x2 - h * Math.cos(a - 0.45)},${y2 - h * Math.sin(a - 0.45)} ${x2 - h * Math.cos(a + 0.45)},${y2 - h * Math.sin(a + 0.45)}`} />
    </g>
  );
};
const Label = ({ x, y, children, anchor = "middle", size = 15 }) => <text x={x} y={y} textAnchor={anchor} fontSize={size} fontWeight="700" fill={ACC} fontFamily="system-ui, sans-serif">{children}</text>;
const Bracket = ({ x1, x2, y, label }) => (
  <g><path d={`M${x1},${y + 8} V${y} H${x2} V${y + 8}`} fill="none" stroke={ACC} strokeWidth="2.2" /><Label x={(x1 + x2) / 2} y={y - 7}>{label}</Label></g>
);

// Rests: d is the length in crotchet beats.
const Rest = ({ x, d }) => {
  const f = "currentColor";
  if (d >= 3 && d !== 4 || d === 2) return <g><rect x={x - 11} y={LINE0 + 2 * SP - 10} width="22" height="10" fill={f} /><line x1={x - 17} x2={x + 17} y1={LINE0 + 2 * SP} y2={LINE0 + 2 * SP} stroke={f} strokeWidth="2.4" />{d === 3 && <circle cx={x + 20} cy={LINE0 + 2 * SP - 10} r="3.6" fill={f} />}</g>;
  if (d === 4) return <g><rect x={x - 11} y={LINE0 + SP} width="22" height="10" fill={f} /><line x1={x - 17} x2={x + 17} y1={LINE0 + SP} y2={LINE0 + SP} stroke={f} strokeWidth="2.4" /></g>;
  if (d === 1) return <path d={`M${x - 5},${LINE0 + 12} L${x + 5},${LINE0 + 27} L${x - 4},${LINE0 + 39} L${x + 6},${LINE0 + 53} C${x - 4},${LINE0 + 47} ${x - 10},${LINE0 + 53} ${x - 2},${LINE0 + 66}`} fill="none" stroke={f} strokeWidth="4.5" strokeLinejoin="round" strokeLinecap="round" />;
  return <g><circle cx={x - 5} cy={LINE0 + 33} r="5" fill={f} /><path d={`M${x - 5},${LINE0 + 37} Q${x + 2},${LINE0 + 40} ${x + 8},${LINE0 + 30} L${x - 2},${LINE0 + 62}`} fill="none" stroke={f} strokeWidth="3" strokeLinecap="round" /></g>;
};

const KIND = { 0.5: "q", 1: "c", 2: "m", 3: "m", 4: "s" };
const PITCHES = ["G4", "B4", "A4", "C5", "G4", "E4", "F4", "D5"];
// Lays out bars of note lengths (in crotchet beats) on a stave; returns geometry for annotations.
const layout = (bars, startX, pad = 0) => {
  let x = startX, k = 0;
  const out = bars.map((bar) => {
    const bx = x + 10;
    const notes = bar.map((d) => { const n = { x: x + 26, d, p: PITCHES[k++ % PITCHES.length] }; x += (d === "W" ? 2.2 : Math.max(Math.abs(d), d < 0 ? 1 : 0.75)) * BW; return n; });
    x += bar.length ? 18 + pad * BW : Math.max(140, 18 + pad * BW);
    return { x1: bx - 6, x2: x, notes };
  });
  return { bars: out, end: x };
};
const BeamedNotes = ({ notes }) => {
  const groups = []; let g = [];
  notes.forEach((n) => { if (n.d === 0.5) g.push(n); else { if (g.length) groups.push(g); g = []; } });
  if (g.length) groups.push(g);
  return <>
    {notes.filter((n) => n.d < 0).map((n, i) => <Rest key={`r${i}`} x={n.x} d={-n.d} />)}
    {notes.filter((n) => n.d === "W").map((n, i) => <Rest key={`w${i}`} x={n.x + 26} d={4} />)}
    {notes.filter((n) => n.d > 0 && n.d !== 0.5).map((n, i) => <Note key={i} x={n.x} p={n.p} kind={KIND[n.d]} dotted={n.d === 3} />)}
    {groups.map((gr, i) => {
      if (gr.length === 1) return <Note key={`b${i}`} x={gr[0].x} p={gr[0].p} kind="q" />;
      const line = fitBeam(gr.map((n) => ({ x: n.x + 10.5, top: yOf(n.p), bottom: yOf(n.p) })), true, 62, 14);
      const x0 = gr[0].x + 9.2, x1 = gr[gr.length - 1].x + 11.8;
      return <g key={`b${i}`}>{gr.map((n, j) => <Note key={j} x={n.x} p={n.p} kind="q" beam={line.at(n.x + 10.5)} />)}
        <polygon points={`${x0},${line.at(x0)} ${x1},${line.at(x1)} ${x1},${line.at(x1) + 7} ${x0},${line.at(x0) + 7}`} fill="currentColor" /></g>;
    })}
  </>;
};

const NAMES = { 0.5: "½", 1: "1", 2: "2", 4: "4" };
// A stave in one time signature: optional bar brackets, barline labels and "adds up" sums.
const Stave = ({ top, bottom, bars, sums, unit = 1, labelBars, labelLines, anatomy, testId, fixedW, minW = "min-w-[540px]", padBeats = 0 }) => {
  const sx = 150, { bars: geo, end } = layout(bars, sx, padBeats);
  const topPad = labelBars || sums || anatomy ? 0 : 30;
  const H = (anatomy ? 230 : 190) + (labelLines ? 80 : 0) + (sums ? 34 : 0), W = fixedW || Math.max(end + 20, anatomy ? 470 : sums ? 580 : 420);
  return (
    <svg viewBox={`-20 ${topPad} ${W + 20} ${H - topPad}`} className={`w-full ${minW} text-foreground`} role="img" aria-label={`${top}/${bottom} stave`} data-testid={testId}>
      <Lines x1={0} x2={end} /><Clef /><TimeSig x={110} top={top} bottom={bottom} />
      {geo.map((b, i) => <g key={i}>
        <BeamedNotes notes={b.notes} />
        <Barline x={b.x2} double={i === geo.length - 1} />
        {labelBars && <Bracket x1={b.x1 + 4} x2={b.x2 - (i === geo.length - 1 ? 14 : 6)} y={28} label={`Bar ${i + 1}`} />}
        {sums && <>
          <Bracket x1={b.x1 + 4} x2={b.x2 - (i === geo.length - 1 ? 14 : 6)} y={28} label={b.notes[0]?.d === "W" ? `whole bar's rest = ${top}` : `${b.notes.map((n) => { const v = NAMES[Math.abs(n.d) / unit] || Math.abs(n.d) / unit; return n.d < 0 ? `(${v})` : v; }).join(" + ")} = ${top}`} />
        </>}
      </g>)}
      {labelLines && <>
        <Arrow x1={geo[0].x2 + 24} y1={BOT + 46} x2={geo[0].x2} y2={BOT + 6} />
        <Label x={geo[0].x2 + 24} y={BOT + 64}>Barline</Label>
        {geo.length > 2 && <><Arrow x1={geo[1].x2 + 24} y1={BOT + 46} x2={geo[1].x2} y2={BOT + 6} /><Label x={geo[1].x2 + 24} y={BOT + 64}>Barline</Label></>}
        <Arrow x1={end - 20} y1={BOT + 82} x2={end - 6} y2={BOT + 6} />
        <Label x={end - 10} y={BOT + 100} anchor="end">Double barline: the end of the music</Label>
      </>}
      {sums && <Label x={sx - 40} y={BOT + 42} anchor="start" size={14}>{`Each bar adds up to the time signature: ${top} ${UNIT[bottom]} beat${top > 1 ? "s" : ""}`}</Label>}
      {anatomy && <>
        <Arrow x1={150} y1={22} x2={124} y2={LINE0 + 6} />
        <Label x={156} y={24} anchor="start">{`Top number ${top}: ${top} beats in every bar`}</Label>
        <Arrow x1={150} y1={BOT + 44} x2={124} y2={BOT - 2} />
        <Label x={156} y={BOT + 50} anchor="start">{`Bottom number ${bottom}: ${UNIT[bottom]} beats`}</Label>
      </>}
    </svg>
  );
};
const UNIT = { 2: "minim", 4: "crotchet", 8: "quaver" };

const Card = ({ children, caption, testId }) => (
  <figure className="min-w-0 max-w-full rounded-2xl border border-border bg-card p-3 sm:p-4" data-testid={testId}>
    <div className="overflow-x-auto">{children}</div>
    {caption && <figcaption className="mt-1 text-center text-xs font-bold text-foreground/80">{caption}</figcaption>}
  </figure>
);

const NoteAnatomy = ({ kind }) => {
  const minim = kind === "m";
  return (
    <Card testId={`diagram-${minim ? "minim" : "crotchet"}-anatomy`} caption={minim ? "Minim (half note) = 2 beats" : "Crotchet (quarter note) = 1 beat"}>
      <svg viewBox="0 0 230 170" className="mx-auto w-56 text-foreground" role="img" aria-label={`Labelled ${minim ? "minim" : "crotchet"}`}>
        <ellipse cx="70" cy="130" rx="22" ry="16" transform="rotate(-20 70 130)" fill={minim ? "none" : "currentColor"} stroke="currentColor" strokeWidth="5" />
        <line x1="89" x2="89" y1="126" y2="18" stroke="currentColor" strokeWidth="4" />
        <Arrow x1="150" y1="50" x2="96" y2="60" /><Label x="156" y="46" anchor="start" size={17}>Stem</Label>
        <Arrow x1="150" y1="140" x2="96" y2="134" /><Label x="156" y="136" anchor="start" size={17}>Notehead</Label>
        <Label x="156" y="156" anchor="start" size={12}>{minim ? "(hollow)" : "(filled in)"}</Label>
      </svg>
    </Card>
  );
};

const TimeSigAnatomy = ({ top = 4, bottom = 4 }) => (
  <Card testId={`diagram-timesig-${top}-${bottom}`} caption="A time signature">
    <svg viewBox="0 0 280 175" className="mx-auto w-full max-w-xs text-foreground" role="img" aria-label={`Labelled ${top}/${bottom} time signature`}>
      <g transform="translate(0 -5)"><Lines x1={10} x2={90} /></g>
      <g transform="translate(-60 -5)"><TimeSig x={110} top={top} bottom={bottom} /></g>
      <Arrow x1="120" y1="85" x2="72" y2="85" /><Label x="126" y="80" anchor="start" size={14}>Top number:</Label><Label x="126" y="97" anchor="start" size={12}>how many beats in a bar</Label>
      <Arrow x1="120" y1="128" x2="72" y2="128" /><Label x="126" y="124" anchor="start" size={14}>Bottom number:</Label><Label x="126" y="141" anchor="start" size={12}>the value of each beat</Label><Label x="126" y="157" anchor="start" size={12}>{`(${bottom} = ${UNIT[bottom]})`}</Label>
    </svg>
  </Card>
);

const TIPS = {
  crotchets: "Now practice drawing crotchets on your manuscript paper: a slightly tilted oval notehead, coloured in, with a straight stem. Draw some on lines and some in spaces, with stems up on the right below the middle line and down on the left above it.",
  rests: "Now practice drawing each rest on your manuscript paper, three times each: the semibreve rest 'hangs' off the 4th line, the minim rest 'sits' on the 3rd line, then the zig-zag crotchet rest and the quaver rest (a 7 with a blob). Draw the matching note next to each one.",
};
const ManuscriptTip = ({ what }) => (
  <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-primary/40 bg-primary/5 p-4" data-testid={`diagram-manuscript-tip-${what}`}>
    <PenLine size={20} className="shrink-0 text-primary" />
    <p className="flex-1 text-sm font-semibold">{TIPS[what]}</p>
    <a href="/media/jt-hub-manuscript-paper.pdf" target="_blank" rel="noreferrer" data-testid={`diagram-manuscript-link-${what}`} className="rounded-full bg-primary px-4 py-1.5 text-xs font-bold text-primary-foreground">Open manuscript paper</a>
  </div>
);

const SIMPLE = [
  { top: 4, bars: [[1, 1, 1, 1], [2, 2]] },
  { top: 3, bars: [[1, 1, 1], [2, 1]] },
  { top: 2, bars: [[1, 1], [2]] },
];
const MIXED = [
  { top: 4, bars: [[2, 1, 1], [1, 2, 1], [1, 1, 2]] },
  { top: 3, bars: [[2, 1], [1, 2], [1, 1, 1]] },
  { top: 2, bars: [[1, 1], [2], [1, 1]] },
];
const MORE = [
  { top: 2, bottom: 2, bars: [[2, 2], [4]], unit: 2 },
  { top: 3, bottom: 2, bars: [[2, 2, 2], [4, 2]], unit: 2 },
  { top: 4, bottom: 2, bars: [[2, 2, 2, 2], [4, 4]], unit: 2 },
  { top: 3, bottom: 8, bars: [[0.5, 0.5, 0.5], [1, 0.5]], unit: 0.5 },
];

const PAIRS = [
  [4, "Semibreve", "Semibreve rest", "4 beats", "'Hangs' off the 4th line. Also called a whole bar's rest: in 3/4 or 2/4 it fills an empty bar, so there it lasts 3 or 2 beats, not 4."],
  [3, "Dotted minim", "Dotted minim rest", "3 beats", "A minim rest with a dot."],
  [2, "Minim", "Minim rest", "2 beats", "'Sits' on the 3rd line."],
  [1, "Crotchet", "Crotchet rest", "1 beat", "A zig-zag shape."],
  [0.5, "Quaver", "Quaver rest", "½ beat", "Looks like a 7 with a blob."],
];
const NoteRestPairs = () => (
  <div className="grid min-w-0 grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-3" data-testid="diagram-note-rest-pairs">
    <p className="col-span-2 text-base font-semibold lg:col-span-3">For every note there's a rest of the same length:</p>
    {PAIRS.map(([d, note, rest, len, tip]) => (
      <figure key={d} className="rounded-2xl border border-border bg-card p-3" data-testid={`diagram-pair-${String(d).replace(".", "-")}`}>
        <svg viewBox="0 45 200 150" className="w-full text-foreground" role="img" aria-label={`${note} and ${rest}`}>
          <Lines x1={0} x2={200} />
          <Note x={50} p="A4" kind={KIND[d]} dotted={d === 3} />
          <Rest x={140} d={d} />
          <Label x={50} y={185} size={13}>Note</Label><Label x={140} y={185} size={13}>Rest</Label>
        </svg>
        <figcaption className="text-center text-xs font-bold text-foreground/80">{note} = {rest} = {len}<span className="block font-normal text-muted-foreground">{tip}</span></figcaption>
      </figure>
    ))}
  </div>
);
const REST_BARS = [
  { top: 4, bars: [[1, -1, 2], [-2, 1, 1], [2, -1, 0.5, 0.5]] },
  { top: 3, bars: [[2, -1], [-1, 1, 1], [1, -2]] },
  { top: 2, bars: [[1, -1], [-0.5, 0.5, 1], [-1, 1]] },
];

const RED = "#EF4444", GREEN = "#10B981";
const Mark = ({ x, y, ok, children }) => (
  <text x={x} y={y} textAnchor="middle" fontSize="17" fontWeight="800" fill={ok ? GREEN : RED} fontFamily="system-ui, sans-serif">{ok ? "\u2713 " : "\u2717 "}{children}</text>
);
const Tie = ({ x1, x2, y, up }) => <path d={`M${x1},${y} Q${(x1 + x2) / 2},${y + (up ? -22 : 22)} ${x2},${y}`} fill="none" stroke={ACC} strokeWidth="3.4" strokeLinecap="round" />;
const TieRightWrong = () => (
  <Card testId="diagram-tie-right-wrong" caption="Draw a tie from notehead to notehead, curving away from the stems. Never join the stems.">
    <svg viewBox="0 10 700 210" className="w-full min-w-[520px] text-foreground" role="img" aria-label="The right and wrong way to draw a tie">
      <Lines x1={10} x2={330} /><Lines x1={370} x2={690} />
      <Mark x={170} y={34} ok>Right</Mark><Mark x={530} y={34}>Wrong</Mark>
      <Note x={110} p="G4" kind="m" /><Note x={230} p="G4" kind="m" /><Tie x1={116} x2={224} y={154} />
      <Note x={470} p="G4" kind="m" /><Note x={590} p="G4" kind="m" /><Tie x1={481} x2={601} y={66} up />
      <Label x={170} y={200}>Notehead to notehead</Label><Label x={530} y={200}>Stem to stem</Label>
    </svg>
  </Card>
);
const TieOrMinim = () => (
  <Card testId="diagram-tie-or-minim" caption="Only tie notes when no single note lasts long enough, or when a note carries over a barline. Two crotchets tied in a bar make 2 beats, so write a minim.">
    <svg viewBox="-20 10 720 210" className="w-full min-w-[520px] text-foreground" role="img" aria-label="Two tied crotchets are wrong; write a minim instead">
      <Lines x1={0} x2={680} /><Clef /><TimeSig x={110} top={4} bottom={4} />
      <Mark x={290} y={34}>Wrong: crotchet tied to crotchet</Mark>
      <Note x={170} p="G4" kind="c" /><Note x={240} p="G4" kind="c" /><Note x={310} p="G4" kind="c" /><Note x={380} p="G4" kind="c" />
      <Tie x1={246} x2={304} y={154} />
      <Barline x={420} />
      <Mark x={550} y={34} ok>Right: a minim</Mark>
      <Note x={455} p="G4" kind="c" /><Note x={525} p="G4" kind="m" /><Note x={625} p="G4" kind="c" />
      <Barline x={676} double />
      <Label x={290} y={200}>1 + (1 + 1) + 1 = 4</Label><Label x={550} y={200}>1 + 2 + 1 = 4</Label>
    </svg>
  </Card>
);

const DIAGRAMS = {
  noteRestPairs: NoteRestPairs,
  restsAddUp: () => (
    <div className="grid min-w-0 grid-cols-1 gap-3" data-testid="diagram-rests-add-up">
      <p className="text-base font-semibold">Rests count too! Every bar still adds up to the time signature (rests are shown in brackets):</p>
      {REST_BARS.map((s) => <Card key={s.top} testId={`diagram-rests-add-up-${s.top}-4`}><Stave top={s.top} bottom={4} bars={s.bars} sums fixedW={820} /></Card>)}
    </div>
  ),
  crotchetAnatomy: () => <NoteAnatomy kind="c" />,
  minimAnatomy: () => <NoteAnatomy kind="m" />,
  crotchetPractice: () => <ManuscriptTip what="crotchets" />,
  restPractice: () => <ManuscriptTip what="rests" />,
  tieRightWrong: TieRightWrong,
  tieOrMinim: TieOrMinim,
  wholeBarRest: () => (
    <div className="grid min-w-0 grid-cols-1 gap-3" data-testid="diagram-whole-bar-rest">
      <p className="text-base font-semibold">The whole bar's rest: when a bar has no notes, the semibreve rest fills it in ANY time signature, so it lasts as long as the bar.</p>
      {[[4, [[1, 1, 2], ["W"], [2, 2]]], [3, [[2, 1], ["W"], [1, 1, 1]]], [2, [[1, 1], ["W"], [2]]]].map(([top, bars]) => (
        <Card key={top} testId={`diagram-whole-bar-rest-${top}-4`} caption={`In ${top}/4 the whole bar's rest lasts ${top} beats${top === 4 ? " (the same as a semibreve)" : ", so here it isn't a 4-beat semibreve rest"}`}>
          <Stave top={top} bottom={4} bars={bars} sums fixedW={720} />
        </Card>
      ))}
    </div>
  ),
  timeSigAnatomy: () => <TimeSigAnatomy />,
  barsDiagram: () => (
    <Card testId="diagram-bars" caption="Bars, barlines and the double barline">
      <Stave top={4} bottom={4} bars={[[1, 1, 1, 1], [2, 1, 1], [2, 2]]} labelBars labelLines testId="diagram-bars-svg" />
    </Card>
  ),
  timeSigExamples: () => (
    <div className="grid min-w-0 grid-cols-1 gap-3" data-testid="diagram-timesig-examples">
      {SIMPLE.map((s) => <Card key={s.top} testId={`diagram-example-${s.top}-4`} caption={`${s.top}/4: ${s.top} crotchet beats in every bar, with a double barline at the end`}><Stave top={s.top} bottom={4} bars={s.bars} anatomy /></Card>)}
    </div>
  ),
  addsUp: () => (
    <div className="grid min-w-0 grid-cols-1 gap-3" data-testid="diagram-adds-up">
      <p className="text-base font-semibold">Mix the note values in each bar, but every bar must add up to the time signature:</p>
      {MIXED.map((s) => <Card key={s.top} testId={`diagram-adds-up-${s.top}-4`}><Stave top={s.top} bottom={4} bars={s.bars} sums /></Card>)}
    </div>
  ),
  moreTimeSigs: () => (
    <div className="grid min-w-0 grid-cols-1 gap-3" data-testid="diagram-more-timesigs">
      {MORE.map((s) => (
        <Card key={`${s.top}${s.bottom}`} testId={`diagram-more-${s.top}-${s.bottom}`} caption={`${s.top}/${s.bottom}: ${s.top} ${UNIT[s.bottom]} beats in every bar`}>
          <Stave top={s.top} bottom={s.bottom} bars={s.bars} unit={s.unit} anatomy />
          <Stave top={s.top} bottom={s.bottom} bars={s.bars} unit={s.unit} sums />
        </Card>
      ))}
      <div className="rounded-2xl border border-primary/50 bg-primary/10 p-4 text-base font-semibold" data-testid="diagram-more-summary">
        Summary: a <span className="text-primary">4</span> at the bottom means you're counting <span className="text-primary">crotchet beats</span>. A <span className="text-primary">2</span> at the bottom means you're counting <span className="text-primary">minim beats</span>, and an <span className="text-primary">8</span> at the bottom means you're counting <span className="text-primary">quaver beats</span>.
      </div>
    </div>
  ),
};

export const NotationDiagram = ({ name }) => {
  const D = DIAGRAMS[name];
  return D ? <div className="mt-5 grid min-w-0 max-w-full grid-cols-1">{<D />}</div> : null;
};

export { Stave as NotationStave, Rest as RestGlyph, Lines as StaveLines, Note as NoteGlyph, PAIRS as NOTE_REST_PAIRS, KIND as NOTE_KIND };
